import _ from 'lodash';
import { zIndexManager } from 'components/sharpr/zIndex/manager/zIndexManager';

/**
 * Class responsible for setting up collapse behavior triggers and targets
 */
export class Collapser {
	/**
	 * Setup on the given element with the given options
	 * @param {angular.element} $element  The angular.element instance that should open and close
	 * @param {Object} options  The options for collapsing
	 * @property {Boolean} isOpen  True to start expanded instead of collapsed
	 * @property {Number} duration  The number of milliseconds for the collapse/expand animation
	 * @property {Boolean} handleClick  If true, setup a trigger element (see `trigger` property)
	 * @property {String} trigger  A CSS selector to select the element to use as a trigger.
	 * If not given, the previous sibling is used
	 */
	constructor($element, options) {
		/**
		 * The element that should expand and collapse
		 * @type {angular.element}
		 */
		this.$element = $element;
		/**
		 * Options for collapsing
		 * @type {Object}
		 */
		this.options = options;
		this.setupTargetNode();
		this.setupClickNode();
	}

	/**
	 * Set up the node that will expand and collapse
	 */
	setupTargetNode() {
		let transValue = this.$element.css('transition');
		let existingTransition = transValue ? transValue + ', ' : '';
		this.$element.css('overflow', 'hidden');
		this.$element.css('transition', existingTransition + `height ${this.options.duration}ms cubic-bezier(.57,0,.43,1)`);
		if (this.options.isOpen) {
			this.$element.css('height', 'auto');
		} else {
			if (this.options.show) {
				setTimeout(() => {
					this.$element.css('height', this.getShowHeight());
				}, 0);
			} else {
				this.$element.css('height', 0);
			}
		}
		this.fitOnMutation();
		let parent = this.$element[0].parentNode;
		while (parent && parent.classList && parent.classList.contains) {
			if (parent.classList.contains('collapser-cloak')) {
				parent.classList.remove('collapser-cloak');
				break;
			}
			parent = parent.parentNode;
		}
	}

	/**
	 * Set up the MutationObserver to update size when elements are added to
	 * or removed from the target node
	 */
	fitOnMutation() {
		if (!this.$element || !this.$element[0]) {
			return;
		}
		let observer = new MutationObserver(
			_.throttle(
				() => {
					if (this.options.isOpen && !this.$element.hasClass('is-animating')) {
						this.fit();
					}
				},
				{ leading: true, trailing: true },
				25
			)
		);
		observer.observe(this.$element[0], {
			childList: true,
			characterData: true,
			subtree: true,
		});
	}

	/**
	 * Set up the target element
	 */
	setupClickNode() {
		if (this.options.trigger) {
			/**
			 * The node whose click triggers collapse
			 * @type {angular.element}
			 */
			this.$clickNode = angular.element(document.querySelector(this.options.trigger));
		} else if (this.$element && this.$element[0]) {
			let node = this.$element[0];
			while ((node = node.previousSibling)) {
				if (node.nodeType === 1) {
					// we found the element node!
					this.$clickNode = angular.element(node);
					break;
				}
			}
		}
		if (this.options.handleClick && this.$clickNode) {
			this.$clickNode.on('click', this.toggle.bind(this));
		}
	}

	/**
	 * Open if closed; close if open
	 */
	toggle() {
		let shouldBeOpen = !this.options.isOpen;
		if (shouldBeOpen) {
			this.open();
		} else {
			this.close();
		}
	}

	/**
	 * Expand the target element
	 */
	open() {
		if (this.options.addZIndex) {
			zIndexManager.bringToTop(this.$element);
		}
		this.$clickNode && this.$clickNode.addClass('is-animating').addClass('is-animating-open');
		this.$element.addClass('is-animating').addClass('is-animating-open');
		this.$element.one('transitionend', () => {
			this.$clickNode && this.$clickNode.removeClass('is-animating').removeClass('is-animating-open').addClass('is-open');
			this.$element.removeClass('is-animating').removeClass('is-animating-open').addClass('is-open');
		});
		this.$element.css('visibility', 'hidden');
		this.$element.css('height', 'auto');
		let newHeight = this.$element[0].clientHeight + 'px';
		this.$element.css('height', this.getShowHeight());
		// we have to wait until the next event loop
		// to set the height or else animation will not apply
		// and to allow the contents to fully render
		setTimeout(() => {
			this.$element.css('visibility', '');
			this.$element.css('height', newHeight);
		}, 16);
		this.options.isOpen = true;
	}

	/**
	 * Collapse the target element
	 */
	close() {
		// console.log('Collapser close');
		this.$clickNode && this.$clickNode.addClass('is-animating').addClass('is-animating-closed').removeClass('is-open');
		this.$element.addClass('is-animating').addClass('is-animating-closed').removeClass('is-open');
		this.$element.one('transitionend', () => {
			this.$clickNode && this.$clickNode.removeClass('is-animating').removeClass('is-animating-closed');
			this.$element.removeClass('is-animating').removeClass('is-animating-closed');
		});
		this.$element.css('height', this.getShowHeight());
		this.options.isOpen = false;
	}

	/**
	 * Get the sum height of all elements that are to remain shown after collapse.
	 * The options.show property contains a comma-sep list of element names.
	 */
	getShowHeight() {
		let showElems = this.options.show.split(',');
		let height = 0;
		showElems.forEach(item => {
			let elem = this.$element.find(item);
			if (!elem.length || !elem[0].offsetHeight) {
				return null;
			}
			height += elem[0].offsetHeight;
		});

		return height + 'px';
	}

	/**
	 * Update the height of the target element to fit its children
	 */
	fit() {
		let oldTransition = this.$element.css('transition');
		this.$element.css('transition', '');
		this.$element.css('height', 0);
		this.$element.css('height', this.$element[0].scrollHeight + 'px');
		setTimeout(() => {
			// add back transition value after to ensure we don't transition
			this.$element.css('transition', oldTransition);
		}, 16);
	}
}
